import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, Phone, MessageCircle, Car, CheckCircle, XCircle, 
  Clock, User, Filter, RefreshCw, ChevronDown, Eye, MapPin
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import api from '../../services/api';
import { toast } from 'sonner';

const ActivityLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [rideSearch, setRideSearch] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/rides/activity-logs?limit=${limit}&offset=${page * limit}`;
      if (categoryFilter !== 'all') {
        url += `&category=${categoryFilter}`;
      }
      if (rideSearch) {
        url += `&ride_id=${rideSearch}`;
      }
      const response = await api.get(url);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      toast.error('Failed to load activity logs');
    }
    setLoading(false);
  }, [categoryFilter, rideSearch, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionIcon = (action) => {
    if (action.includes('call')) return <Phone className="w-4 h-4 text-green-500" />;
    if (action.includes('text')) return <MessageCircle className="w-4 h-4 text-blue-500" />;
    if (action.includes('completed')) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (action.includes('cancelled')) return <XCircle className="w-4 h-4 text-red-500" />;
    if (action.includes('accepted') || action.includes('assigned')) return <User className="w-4 h-4 text-purple-500" />;
    return <Activity className="w-4 h-4 text-[var(--primary)]" />;
  };

  const getActionBadge = (action, category) => {
    if (category === 'communication') {
      if (action.includes('call')) {
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">Call</span>;
      }
      if (action.includes('text')) {
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">Text</span>;
      }
    }
    if (category === 'ride') {
      if (action.includes('completed')) {
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">Completed</span>;
      }
      if (action.includes('cancelled')) {
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">Cancelled</span>;
      }
      if (action.includes('accepted')) {
        return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">Accepted</span>;
      }
    }
    return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs capitalize">{action.replace(/_/g, ' ')}</span>;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-primary)]">Activity Logs</h2>
          <p className="text-sm text-[var(--text-muted)]">Track ride events and customer-driver communication</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchLogs}
          disabled={loading}
          className="border-[var(--primary)] text-[var(--primary)]"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Category</label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="bg-[var(--background)] border-[var(--border-primary)]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="ride">Ride Events</SelectItem>
                <SelectItem value="communication">Communication</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Search Ride Reference</label>
            <Input
              placeholder="e.g., RIDE-00036"
              value={rideSearch}
              onChange={(e) => setRideSearch(e.target.value)}
              className="bg-[var(--background)] border-[var(--border-primary)]"
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-[var(--primary)]">{total}</div>
          <div className="text-xs text-[var(--text-muted)]">Total Events</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-green-500">
            {logs.filter(l => l.action?.includes('call')).length}
          </div>
          <div className="text-xs text-[var(--text-muted)]">Calls</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-blue-500">
            {logs.filter(l => l.action?.includes('text')).length}
          </div>
          <div className="text-xs text-[var(--text-muted)]">Texts</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-purple-500">
            {logs.filter(l => l.category === 'ride').length}
          </div>
          <div className="text-xs text-[var(--text-muted)]">Ride Events</div>
        </div>
      </div>

      {/* Logs List */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center">
            <Activity className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
            <p className="text-[var(--text-muted)]">No activity logs found</p>
            <p className="text-sm text-[var(--text-muted)] mt-1">Activity will appear here when customers and drivers interact</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-primary)]">
            {logs.map((log) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 hover:bg-[var(--background-subtle)] cursor-pointer transition-colors"
                onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[var(--background)] flex items-center justify-center flex-shrink-0">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[var(--text-primary)]">{log.description}</span>
                      {getActionBadge(log.action, log.category)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-[var(--text-muted)]">
                      {log.ride_reference && (
                        <span className="flex items-center gap-1">
                          <Car className="w-3 h-3" />
                          {log.ride_reference}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.actor_name || 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(log.timestamp)}
                      </span>
                    </div>
                    
                    {/* Expanded Details */}
                    {selectedLog?.id === log.id && log.details && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="mt-3 p-3 bg-[var(--background)] rounded-lg text-sm"
                      >
                        <div className="grid grid-cols-2 gap-2">
                          {log.details.direction && (
                            <div>
                              <span className="text-[var(--text-muted)]">Direction: </span>
                              <span className="text-[var(--text-primary)]">
                                {log.details.direction === 'customer_to_driver' ? 'Customer → Driver' : 'Driver → Customer'}
                              </span>
                            </div>
                          )}
                          {log.details.contact_type && (
                            <div>
                              <span className="text-[var(--text-muted)]">Type: </span>
                              <span className="text-[var(--text-primary)] capitalize">{log.details.contact_type}</span>
                            </div>
                          )}
                          {log.details.ride_status && (
                            <div>
                              <span className="text-[var(--text-muted)]">Ride Status: </span>
                              <span className="text-[var(--text-primary)] capitalize">{log.details.ride_status.replace(/_/g, ' ')}</span>
                            </div>
                          )}
                          {log.details.total_fare && (
                            <div>
                              <span className="text-[var(--text-muted)]">Fare: </span>
                              <span className="text-green-500 font-semibold">${log.details.total_fare.toFixed(2)}</span>
                            </div>
                          )}
                          {log.details.driver_earnings && (
                            <div>
                              <span className="text-[var(--text-muted)]">Driver Earnings: </span>
                              <span className="text-[var(--text-primary)]">${log.details.driver_earnings.toFixed(2)}</span>
                            </div>
                          )}
                          {log.details.target_phone && (
                            <div>
                              <span className="text-[var(--text-muted)]">Phone: </span>
                              <span className="text-[var(--text-primary)]">***-***-{log.details.target_phone}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${selectedLog?.id === log.id ? 'rotate-180' : ''}`} />
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {total > limit && (
          <div className="p-4 border-t border-[var(--border-primary)] flex items-center justify-between">
            <span className="text-sm text-[var(--text-muted)]">
              Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogsTab;
